import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    useSearchSuggestions,
    useProductSearch,
    useShopperContext,
    useUsid,
    useCustomer,
    useCustomerId,
} from '@salesforce/commerce-sdk-react'
import {
    InputGroup,
    InputRightElement,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Box,
    Flex,
    HStack,
    useMultiStyleConfig,
    useOutsideClick,
    Text,
    VStack,
    Button,
} from '@chakra-ui/react'
import InputComponent from '../field/input-component'
import SearchSuggestions from './partials/search-suggestions'
import {SearchIcon} from '../icons'
import {
    getSessionJSONItem,
    isServer,
    noop,
    setSessionJSONItem,
} from '@salesforce/retail-react-app/app/utils/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import debounce from 'lodash/debounce'
import {RECENT_SEARCH_KEY, RECENT_SEARCH_LIMIT} from '@salesforce/retail-react-app/app/constants'
import formatSuggestions from './partials/format-suggestions'
import {searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {FormattedMessage, useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {decodeHtmlEntities} from '../../utils/utils'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import LoadingSpinner from '../loading-spinner'

const Search = ({
    searchInputPlaceholder,
    variant = 'default',
    shouldRemoveLabel = false,
    isManualsPageSearch = false,
    isSearchModal = false,
    isProductRegistration = false,
    setProductQuery = noop,
    setShowProductGrid = noop,
    setSuggestionRegistration = noop,
    setProductSelect,
    setSearchResults = noop,
    setCategorySuggestions,
    setModalNavigation,
    setShowCategories,
    onCloseModal,
    onSubmitCustom = null,
    onSearchInputChangeCustom = null,
    customSuggestionData = null,
    showSpellingMessage = false,
    id = '',
    ariaLabel = 'Search Input',
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const styles = useMultiStyleConfig('Search', {variant})
    const {addLayer} = useGoogleTagManager()
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigation()
    const intl = useIntl()
    const SUGGESTIONS_LENGTH = 5
    const faqStyles = useMultiStyleConfig('Faq', {})

    const [searchStatusMessage, setSearchStatusMessage] = useState('')

    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()
    const isNotShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const customerId = useCustomerId()
    const {usid} = useUsid()
    const {data: shopperContext, isFetching: isFetchingShopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    const {data: customer} = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer},
    )

    const {searchAndTitleStyles} = faqStyles || {}

    const searchSuggestion = useSearchSuggestions(
        {parameters: {q: searchQuery}},
        {enabled: searchQuery?.length >= SUGGESTIONS_LENGTH},
    )

    const searchInputRef = useRef()
    const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY)

    let searchSuggestionSource =
        customSuggestionData !== null ? customSuggestionData : searchSuggestion.data

    const searchSuggestions = useMemo(
        () => formatSuggestions(searchSuggestionSource, searchInputRef?.current?.value),
        [searchSuggestionSource],
    )

    // check if popover should open if we have suggestions
    useEffect(() => {
        shouldOpenPopover()
    }, [searchQuery, searchSuggestionSource])

    const searchSuggestionsAvailable =
        Boolean(searchSuggestions) &&
        (searchSuggestions?.categorySuggestions?.length ||
            searchSuggestions?.phraseSuggestions?.length ||
            searchSuggestions?.productSuggestions?.length ||
            searchSuggestions?.articleSuggestions?.length)

    const saveRecentSearch = (searchText) => {
        // Get recent searches or an empty array if undefined.
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []
        // Check if term is already in the saved searches
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase()
        })
        // Create a new array consisting of the search text and up to 4 other resent searches.
        // I'm assuming the order is newest to oldest.
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)
        // Replace the save resent search with the updated value.
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    const debouncedSearch = useMemo(
        () =>
            debounce((input) => {
                if ((isSearchModal || isProductRegistration) && !input.endsWith('*')) {
                    input += '*'
                }
                setSearchQuery(input)
            }, 150),
        [],
    )

    const onSearchChange = async (e) => {
        const input = e.target.value
        if (input.length >= SUGGESTIONS_LENGTH) {
            if (searchQuery !== input) {
                debouncedSearch(input)
            }
        } else {
            if (searchQuery !== '') {
                setSearchQuery('')
            }
        }
    }

    const clearInput = () => {
        searchInputRef.current.blur()
        setIsOpen(false)

        document.body.focus()
    }

    const refine = []
    if (isSearchModal) {
        if (!isNotShoppableCountry) refine.push('price=(0.01..9999999)')
        refine.push('c_skuEnabledForCountry=true')

        const isCorporate = shopperContext?.customQualifiers?.isCorporate === 'true'
        const isEmployee = customer?.c_isEmployee

        if (isCorporate) {
            refine.push('c_visibleByCorporate=true')
        }
        if (isEmployee) {
            refine.push('c_visibleByEmployee=true')
        }
        if (!isCorporate && !isEmployee) {
            refine.push('c_visibleByAll=true')
        }
    } else if (isProductRegistration) {
        refine.push('c_enabledForReg=true')
    }

    const {
        data: productSearchResult,
        isFetching,
        refetch: refetchproductSearchResult,
    } = useProductSearch(
        {
            parameters: {
                q: searchQuery,
                perPricebook: true,
                allVariationProperties: true,
                expand: ['represented_products', 'page_meta_tags'],
                ...{refine: refine},
                locale: locale.id,
                site: site.id,
                currency,
                limit: 8,
                c_isProductReg: isProductRegistration,
            },
        },
        {
            enabled: Boolean(
                (isSearchModal || isProductRegistration || isManualsPageSearch) &&
                    searchQuery.length > 0,
            ),
            keepPreviousData: true,
        },
    )

    const onSubmitSearch = async (e) => {
        e.preventDefault()
        // Avoid blank spaces to be searched
        let searchText = searchInputRef.current.value.trim()
        // Avoid empty string searches
        if (searchText.length < 4) return
        if (isProductRegistration && finalSuggestions && finalSuggestions.length === 0) {
            setSearchResults(undefined)
            return
        }

        if (isProductRegistration && !searchText.endsWith('*')) {
            searchText += '*'
        }
        saveRecentSearch(searchText)
        clearInput()

        if (isSearchModal) {
            setSearchResults(productSearchResult || [])
            const searchUrl = searchUrlBuilder(searchText)
            setModalNavigation(searchUrl)
            setShowCategories(false)
            await addLayer(
                basic_gtm_event.bind(this, {
                    event: 'internalSearch',
                    eventCategory: 'engagement',
                    eventAction: 'internalSearch-result',
                    eventLabel: searchText,
                    resultsAmount: productSearchResult?.total || 0,
                }),
            )
        }

        if (isProductRegistration) {
            setSearchResults(productSearchResult || [])
            setProductSelect(searchText)
        }

        if (isManualsPageSearch) {
            setProductQuery(searchText)
            setShowProductGrid(true)
        }
    }

    const [isCategorySubmitted, setIsCategorySubmitted] = useState(false)

    const onSuggestedCategorySubmit = async (e, categoryName) => {
        e.preventDefault()
        searchInputRef.current.value = decodeHtmlEntities(categoryName)
            .replace(/<\/?[^>]+(>|$)/g, '')
            .trim()
        let searchText = searchInputRef.current.value
        setSearchQuery(searchText)
        setIsCategorySubmitted(true)
        clearInput()
    }

    useEffect(() => {
        if (searchQuery.length >= SUGGESTIONS_LENGTH && isCategorySubmitted) {
            handleCategorySearch(searchQuery)
        }
    }, [isCategorySubmitted])

    const handleCategorySearch = async (searchText) => {
        if (isSearchModal) {
            const refetchProducts = await refetchproductSearchResult()
            setSearchResults(refetchProducts.data || [])
            const searchUrl = searchUrlBuilder(searchText)
            setModalNavigation(searchUrl)
            setShowCategories(false)
            await addLayer(
                basic_gtm_event.bind(this, {
                    event: 'internalSearch',
                    eventCategory: 'engagement',
                    eventAction: 'internalSearch-result',
                    eventLabel: searchText,
                    resultsAmount: productSearchResult?.total || 0,
                }),
            )
        }
        if (isProductRegistration) {
            const refetchProducts = await refetchproductSearchResult()
            setSearchResults(refetchProducts.data || [])
        }
        if (isManualsPageSearch) {
            setProductQuery(searchText)
            setShowProductGrid(true)
        }
        setIsCategorySubmitted(false)
    }

    const closeAndNavigate = (suggestion) => {
        clearInput()
        setIsOpen(false)

        document.body.focus()

        if (isSearchModal && onCloseModal) {
            onCloseModal()
        }
        if (suggestion && isProductRegistration) {
            setSuggestionRegistration(suggestion)
            setSearchResults(suggestion.productId || [])
            setProductSelect(suggestion?.representedProduct?.id)
        }
        if (
            (suggestion?.c_variationGroup_slugUrl || suggestion?.c_slugUrl) &&
            !isManualsPageSearch &&
            !isProductRegistration
        ) {
            // TODO: replace with productUrlBuilder later
            const pid = suggestion?.c_variants?.[0]?.productId
            navigate(
                `${suggestion?.c_variationGroup_slugUrl || suggestion?.c_slugUrl}${
                    pid ? `?pid=${pid}` : ''
                }`,
            )
        }

        // FAQs redirect
        if (suggestion?.link) {
            addLayer(
                basic_gtm_event.bind(this, {
                    event: 'generic',
                    eventCategory: 'engagement',
                    eventAction: 'paperless-faq_single',
                    eventLabel: suggestion.name || '',
                }),
            )
            navigate(suggestion.link)
        }

        if (suggestion && isManualsPageSearch) navigate(`/s/${suggestion?.productId}`)
    }

    const shouldOpenPopover = () => {
        const isInputFocused = document.activeElement === searchInputRef.current
        //const hasRecentSearches = recentSearches?.length > 0
        const hasSearchSuggestions =
            searchSuggestionsAvailable && searchInputRef.current?.value?.length > 0
        //setIsOpen(isInputFocused && (hasRecentSearches || hasSearchSuggestions))
        setIsOpen(isInputFocused && hasSearchSuggestions)
    }

    const onSearchInputChange = (e) => {
        onSearchChange(e)
        shouldOpenPopover()
        if (isSearchModal) {
            setSearchResults([])
        }
        if (searchSuggestions?.categorySuggestions?.length && isSearchModal) {
            setCategorySuggestions(searchSuggestions?.categorySuggestions)
        }
    }

    const popoverRef = useRef()
    const searchFormRef = useRef()
    const popoverContentRef = useRef(null)

    useOutsideClick({
        ref: popoverRef,
        handler: (e) => {
            if (
                !popoverRef.current?.contains(e.target) &&
                !searchFormRef.current?.contains(e.target)
            ) {
                setIsOpen(false)
            }
        },
    })

    // Override PopoverContent role from dialog to listbox (once)
    useEffect(() => {
        if (isOpen && !popoverContentRef.current) {
            const popoverContent = popoverRef.current?.querySelector('[role="dialog"]')
            if (popoverContent) {
                popoverContent.setAttribute('role', 'listbox')
                popoverContentRef.current = popoverContent
            }
        }
    }, [isOpen])

    const handleOnSubmitSearch = onSubmitCustom !== null ? onSubmitCustom : onSubmitSearch

    const handleOnInputChange =
        onSearchInputChangeCustom !== null ? onSearchInputChangeCustom : onSearchInputChange

    let finalSuggestions = []

    if (isSearchModal || isManualsPageSearch || isProductRegistration) {
        const filteredHits = productSearchResult?.hits?.filter(
            (hit) =>
                hit?.c_skipHit !== true && !(hit?.productType && hit?.productType?.set === true), // Exclude product sets
        )

        finalSuggestions = [].concat(searchSuggestions?.categorySuggestions, filteredHits)
    } else if (searchSuggestions.articleSuggestions) {
        finalSuggestions = searchSuggestions?.articleSuggestions
    }

    finalSuggestions = finalSuggestions?.filter(Boolean)

    // Update status message for screen readers when search results change
    useEffect(() => {
        if (isProductRegistration && finalSuggestions && searchQuery.length >= SUGGESTIONS_LENGTH) {
            const count = finalSuggestions.length
            const message = intl.formatMessage(
                {
                    id: 'search.results_status',
                    defaultMessage:
                        '{count, plural, =0 {No results found} one {One result found} other {{count} results found}}',
                },
                {count},
            )
            setSearchStatusMessage(message)
        } else {
            setSearchStatusMessage('')
        }
    }, [finalSuggestions, searchQuery, isProductRegistration, intl])

    const handleInputKeyDown = (e) => {
        if (!isOpen || !isProductRegistration) return

        if (e.key === 'Escape') {
            e.preventDefault()
            setIsOpen(false)
            searchInputRef.current?.focus()
        }
    }

    const handleFocusLastFromSuggestion = () => {
        searchInputRef.current?.focus()
    }

    return (
        <Box {...styles.container} ref={popoverRef} id={id}>
            {/* Screen reader announcement for search results */}
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                position="absolute"
                left="-10000px"
                width="1px"
                height="1px"
                overflow="hidden"
            >
                {searchStatusMessage}
            </Box>
            <Popover
                isOpen={isOpen}
                isLazy
                initialFocusRef={searchInputRef}
                placement="bottom-start"
                matchWidth
                closeOnBlur={true}
                onClose={() => setIsOpen(false)}
            >
                {isFetchingShopperContext && <LoadingSpinner />}
                <PopoverTrigger>
                    <form
                        ref={searchFormRef}
                        onSubmit={(e) => handleOnSubmitSearch(e, searchInputRef, finalSuggestions)}
                        style={isManualsPageSearch ? {...styles.helpTextGap} : {}}
                    >
                        <HStack {...styles.searchHStack}>
                            <InputGroup {...styles.searchInputGroup}>
                                <InputComponent
                                    id="search-input"
                                    name="search-input"
                                    onChange={(e) => handleOnInputChange(e)}
                                    label={searchInputPlaceholder}
                                    shouldRemoveLabel={shouldRemoveLabel}
                                    type="text"
                                    ref={searchInputRef}
                                    data-testid="search-input"
                                    role="combobox"
                                    aria-label={ariaLabel}
                                    aria-expanded={isOpen}
                                    aria-controls="sf-suggestion-popover"
                                    aria-haspopup="listbox"
                                    aria-autocomplete="list"
                                    aria-invalid={showSpellingMessage ? 'true' : 'false'}
                                    aria-describedby={
                                        showSpellingMessage ? 'search-error-message' : undefined
                                    }
                                    // iOS Safari auto-zooms inputs with font-size < 16px.
                                    // @supports (-webkit-touch-callout: none) matches only
                                    // iOS Safari, so the 16px override is invisible to every
                                    // other platform (Android, desktop stay at 12px).
                                    // We do it here in the search bar because the auto zoom blocks the user from
                                    // exiting the search bar after focusing it.
                                    sx={{
                                        '@supports (-webkit-touch-callout: none)': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            if (
                                                !popoverRef.current?.contains(
                                                    document.activeElement,
                                                )
                                            ) {
                                                setIsOpen(false)
                                            }
                                        }, 200)
                                    }}
                                    onKeyDown={handleInputKeyDown}
                                />
                                <InputRightElement {...styles.rightElement}>
                                    <Button
                                        type="button"
                                        aria-label={intl.formatMessage({
                                            id: 'search.button.aria_label',
                                            defaultMessage: 'Search',
                                        })}
                                        variant="ghost"
                                        onClick={(e) =>
                                            handleOnSubmitSearch(
                                                e,
                                                searchInputRef,
                                                finalSuggestions,
                                            )
                                        }
                                        tabIndex={0}
                                        _focusVisible={{boxShadow: 'outline', outline: 'none'}}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Tab' &&
                                                !e.shiftKey &&
                                                isOpen &&
                                                isProductRegistration
                                            ) {
                                                const firstSuggestion =
                                                    popoverRef.current?.querySelector(
                                                        '[role="option"]',
                                                    )
                                                if (firstSuggestion) {
                                                    e.preventDefault()
                                                    firstSuggestion.focus()
                                                }
                                            }
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </InputRightElement>
                            </InputGroup>
                        </HStack>
                        {showSpellingMessage && (
                            <VStack
                                {...styles.spellingMessageGap}
                                role="alert"
                                id="search-error-message"
                            >
                                <Text {...searchAndTitleStyles?.errorMessage}>
                                    <FormattedMessage
                                        id="faq_page.search.error_message"
                                        defaultMessage="Check your spelling or try an alternative term"
                                    />
                                </Text>
                            </VStack>
                        )}
                    </form>
                </PopoverTrigger>
                <PopoverContent {...styles.popoverContent} data-testid="sf-suggestion-popover">
                    <Flex
                        data-testid="sf-suggestion-popover"
                        display={isOpen || searchInputRef?.value?.length > 0 ? 'block' : 'none'}
                        {...styles.modalSuggestions}
                    >
                        {searchSuggestionSource === null || isFetching ? (
                            <LoadingSpinner />
                        ) : (
                            <SearchSuggestions
                                closeAndNavigate={closeAndNavigate}
                                recentSearches={recentSearches}
                                searchSuggestions={finalSuggestions}
                                onSuggestedCategorySubmit={onSuggestedCategorySubmit}
                                onFocusLast={handleFocusLastFromSuggestion}
                            />
                        )}
                    </Flex>
                </PopoverContent>
            </Popover>
        </Box>
    )
}

Search.propTypes = {
    searchInputPlaceholder: PropTypes.any,
    variant: PropTypes.string,
    shouldRemoveLabel: PropTypes.bool,
    isManualsPageSearch: PropTypes.bool,
    isSearchModal: PropTypes.bool,
    isProductRegistration: PropTypes.bool,
    setProductSelect: PropTypes.func,
    setProductQuery: PropTypes.func,
    setShowProductGrid: PropTypes.func,
    setSearchResults: PropTypes.func,
    setCategorySuggestions: PropTypes.func,
    setModalNavigation: PropTypes.func,
    setShowCategories: PropTypes.func,
    setSuggestionRegistration: PropTypes.func,
    onCloseModal: PropTypes.func,
    onSubmitCustom: PropTypes.func,
    onSearchInputChangeCustom: PropTypes.func,
    customSuggestionData: PropTypes.object,
    showSpellingMessage: PropTypes.bool,
    id: PropTypes.string,
    ariaLabel: PropTypes.string,
}

export default Search
